import { Input, OutlinedInput, OutlinedInputProps, SxProps, Theme } from '@mui/material';
import React from 'react';
import characters from 'src/constants/characters';
import { ClickElementType } from 'src/lib/analytics/events';
import { Typography } from '../Typography';
import { Field, FieldProps } from './Field';
import withFormConsumer from './withFormConsumer';

type TextFieldVariant = 'outlined' | 'text';
export interface TextFieldProps
    extends
        Pick<
            FieldProps,
            | 'editable'
            | 'error'
            | 'helperText'
            | 'label'
            | 'labelAdornment'
            | 'inlineLabelAdornment'
            | 'tooltip'
            | 'wide'
            | 'required'
        >,
        Pick<
            OutlinedInputProps,
            | 'autoComplete'
            | 'autoFocus'
            | 'className'
            | 'defaultValue'
            | 'disabled'
            | 'id'
            | 'multiline'
            | 'name'
            | 'onMouseDown'
            | 'placeholder'
            | 'rows'
            | 'maxRows'
            | 'style'
            | 'type'
            | 'onBlur'
            | 'onChange'
            | 'onClick'
            | 'onKeyDown'
            | 'value'
            | 'size'
            | 'startAdornment'
            | 'endAdornment'
            | 'inputProps'
        > {
    trimWhiteSpace?: boolean;
    variant?: TextFieldVariant;
    emptyNode?: React.ReactNode;
    inputSx?: SxProps<Theme>;
    autoCapitalize?: string;
    autoCorrect?: string;
    spellCheck?: boolean;
}

export const Text = ({
    editable = true,
    error,
    helperText,
    label,
    labelAdornment,
    inlineLabelAdornment,
    tooltip,
    value,
    onChange,
    trimWhiteSpace = false,
    wide = false,
    required = false,
    variant = 'outlined',
    emptyNode = characters.emdash,
    inputSx,
    autoCapitalize,
    autoCorrect,
    spellCheck,
    // @ts-expect-error - disableUnderline is used by some text-variant callers; drop it from the outlined path
    disableUnderline,
    inputProps: inputElementProps,
    // @ts-expect-error - InputProps is not in TextFieldProps but may be passed by callers; destructure to prevent DOM leak
    InputProps: _InputProps,
    ...inputProps
}: TextFieldProps) => {
    const baseOutlinedSx: SxProps<Theme> = {
        backgroundColor: (theme) => theme.resqPalette.surface.default,
        color: (theme) => theme.resqPalette.text.default,
        '& .MuiOutlinedInput-input, & .MuiInputBase-input': {
            color: (theme) => theme.resqPalette.text.default,
            '&::placeholder': {
                color: (theme) => theme.resqPalette.text.placeHolder,
                WebkitTextFillColor: (theme) => theme.resqPalette.text.placeHolder,
                opacity: 1,
            },
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: (theme) => theme.resqPalette.border.focused,
            borderWidth: 2,
        },
        '&.Mui-disabled': {
            backgroundColor: (theme) => theme.resqPalette.surface.subdued,
            '& .MuiOutlinedInput-input': {
                color: (theme) => theme.resqPalette.text.disabled,
            },
        },
    };
    const resolvedOutlinedSx: SxProps<Theme> = inputSx
        ? ([baseOutlinedSx, inputSx] as SxProps<Theme>)
        : baseOutlinedSx;

    return (
        <Field
            editable={editable}
            error={error}
            label={label}
            labelAdornment={labelAdornment}
            inlineLabelAdornment={inlineLabelAdornment}
            tooltip={tooltip}
            helperText={helperText}
            wide={wide}
            required={required}
            elementType={ClickElementType.TEXT_INPUT}
            name={inputProps.name}
        >
            {!editable ? (
                <Typography role="body" sx={{ overflowWrap: 'break-word' }}>
                    {value ? String(value) : emptyNode}
                </Typography>
            ) : variant === 'text' ? (
                <Input
                    error={!!error}
                    fullWidth
                    disableUnderline={disableUnderline}
                    inputProps={{
                        ...inputElementProps,
                        autoCapitalize,
                        autoCorrect,
                        spellCheck,
                        onBlur: (event) => {
                            if (trimWhiteSpace && onChange) {
                                event.target.value = event.target.value.trim();
                                onChange(event);
                            }
                            inputElementProps?.onBlur?.(event);
                        },
                    }}
                    value={value}
                    onChange={onChange}
                    {...inputProps}
                />
            ) : (
                <OutlinedInput
                    sx={resolvedOutlinedSx}
                    error={!!error}
                    fullWidth
                    inputProps={{
                        ...inputElementProps,
                        autoCapitalize,
                        autoCorrect,
                        spellCheck,
                        onBlur: (event) => {
                            if (trimWhiteSpace && onChange) {
                                event.target.value = event.target.value.trim();
                                onChange(event);
                            }
                            inputElementProps?.onBlur?.(event);
                        },
                    }}
                    value={value}
                    onChange={onChange}
                    {...inputProps}
                />
            )}
        </Field>
    );
};
export const TextField = withFormConsumer<NonNullable<OutlinedInputProps['value']>, TextFieldProps>(
    Text,
);
